/** Getting-started checklist (OutlierOS components/onboarding/checklist.tsx): steps detected from real activity. */
import { useEffect, useState } from 'react';
import { CheckCircle2, Circle, Rocket, X } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { cn } from '@vw/ui';
import { api } from '../api';
import { Link } from '../router';

interface Status { steps: Array<{ id: string; done: boolean }>; done: number; total: number; complete: boolean }
const DISMISS_KEY = 'vw_onboarding_dismissed';
const COPY: Record<string, { title: string; text: string; cta: string; href: string; external?: boolean }> = {
  connect_extension: { title: 'Install & connect the Chrome extension', text: `Add ${BRAND.name} to Chrome, then click "Connect" in its side panel.`, cta: 'Set up the extension', href: '/settings#extension' },
  scan_creator: { title: 'Scan your first creator', text: "Open any creator's Reels tab on instagram.com and press Scan in the side panel.", cta: 'Open Instagram', href: 'https://www.instagram.com/', external: true },
  transcribe_reel: { title: 'Transcribe a viral reel', text: 'Open a reel with a Wise Score of 3×+ in a creator report and transcribe it to get its spoken hook.', cta: 'Open Your Feed', href: '/feed' },
  share_or_collect: { title: 'Save, share or collect', text: 'Add reels to a collection or share a creator report link.', cta: 'Open Collections', href: '/collections' },
};

export function OnboardingChecklist({ variant }: { variant: 'card' | 'page' }) {
  const [s, setS] = useState<Status | null>(null);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; } });
  useEffect(() => { api<Status>('/api/onboarding').then(setS, () => setS(null)); }, []);
  if (!s || (variant === 'card' && (s.complete || dismissed))) return null;
  return (
    <section className={cn('rounded-3xl border border-border bg-card shadow-xs', variant === 'page' ? 'p-6 md:p-8' : 'p-5')}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Rocket className="h-5 w-5" /></span>
        <div className="flex-1">
          <h2 className="font-extrabold">{s.complete ? "You're all set 🎉" : `Get started with ${BRAND.name}`}</h2>
          <p className="text-xs text-muted-foreground">{s.done} of {s.total} done · about 3 minutes</p>
        </div>
        {variant === 'card' && <button aria-label="Hide getting started" onClick={() => { setDismissed(true); try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ } }} className="rounded-lg p-1 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>}
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(s.done / s.total) * 100}%` }} /></div>
      <ol className={cn('mt-4 grid gap-2', variant === 'card' && 'md:grid-cols-2')}>
        {s.steps.map((st) => {
          const c = COPY[st.id]!;
          return (
            <li key={st.id} className="flex gap-3 rounded-2xl border border-border p-3">
              {st.done ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />}
              <div className="min-w-0">
                <p className={cn('text-sm font-bold', st.done && 'text-muted-foreground line-through')}>{c.title}</p>
                {!st.done && <>
                  <p className="text-xs text-muted-foreground">{c.text}</p>
                  {c.external ? <a href={c.href} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-semibold text-primary">{c.cta} →</a>
                    : <Link to={c.href} className="mt-1 inline-block text-xs font-semibold text-primary">{c.cta} →</Link>}
                </>}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
